import React, { FC, useCallback } from "react";
import {
  Checkbox,
  DropdownInput,
  QuestionTooltip,
  QuestionTooltipProps,
} from "component-library";
import { getAgeRangeOptions } from "shared-utils";
import { PlayingAge as PlayingAgeEnum } from "shared-types";
import { useTranslation } from "i18n";
import { FEATURE_FLAGS, useLaunchDarklyFlags } from "shared-services";
import {
  IEditBasicInfoContextProps,
  withEditBasicInfoContext,
} from "../../../../../../contexts/EditBasicInfoContext";
import { ControlSection } from "../../../../../Common";
import { IPlayingAgeViewModel } from "../../../../models/views";

export const AGE_DIFFERENCE_WARNING_THRESHOLD = 15;

export interface IPlayingAgeContent {
  playingAgeSectionName: string;
  playingFromYearField: string;
  playingToYearField: string;
  publishActualAgeInfo: string;
  year: string;
  years: string;
  playingAgeTooltip: QuestionTooltipProps;
  playingAgeWarning: string;
}

export interface IPlayingAgeProps extends IEditBasicInfoContextProps {
  model: IPlayingAgeViewModel;
  content: IPlayingAgeContent;
  handleChanges: (model: IPlayingAgeViewModel) => void;
}

export const PlayingAge: FC<IPlayingAgeProps> = ({
  model,
  content,
  sharedState: { isPlayingAgeWarned },
  handleChanges,
  sharedState,
}) => {
  const { t } = useTranslation();
  const isYPsPlayingAgeEnabled = useLaunchDarklyFlags(
    FEATURE_FLAGS.YPsPlayingAgeMin
  );

  const { fromAge, toAge, isActualAgeVisible } = model;

  const isAgeDifferenceTooBig = (fromAge: number, toAge: number): boolean => {
    const ageDifference = fromAge >= 0 && toAge >= 0 ? toAge - fromAge : 0;

    return ageDifference > AGE_DIFFERENCE_WARNING_THRESHOLD;
  };

  const inputChanged = useCallback(
    (name: string) => (value: any) => {
      const updatedModel = { ...model, [name]: value };
      const { fromAge, toAge } = updatedModel;

      const isPlayingAgeWarned = isAgeDifferenceTooBig(fromAge, toAge);

      if (updatedModel[name] !== model[name]) {
        handleChanges(updatedModel);
        sharedState.changeSharedState({ isPlayingAgeWarned });
      }
    },
    [model, handleChanges, sharedState]
  );

  const { yearsFrom, yearsTo } = getAgeRangeOptions({
    t,
    minAge: isYPsPlayingAgeEnabled
      ? PlayingAgeEnum.YPPlayingAgeMinYrs
      : PlayingAgeEnum.PlayingAgeMinYrs,
    selectedFrom: fromAge,
    selectedTo: toAge,
  });

  return (
    <ControlSection name={content.playingAgeSectionName}>
      <div className="c-basic-info-form__playing-age">
        <div className="c-basic-info-form__playing-age-to">
          <DropdownInput<number>
            variant="select"
            id="playingAge"
            label={content.playingFromYearField}
            options={yearsFrom}
            name="playingAge"
            warned={isPlayingAgeWarned}
            validationMessage={content.playingAgeWarning}
            isFormSubmitted={true}
            value={fromAge}
            valueChanged={inputChanged("fromAge")}
            annex={
              content.playingAgeTooltip && (
                <QuestionTooltip {...content.playingAgeTooltip} />
              )
            }
          />
        </div>
        <div className="c-basic-info-form__playing-age-from">
          <DropdownInput<number>
            id="playingAgeFrom"
            variant="select"
            label={content.playingToYearField}
            options={yearsTo}
            name="playingAgeFrom"
            value={toAge}
            valueChanged={inputChanged("toAge")}
          />
        </div>
        <div className="c-basic-info-form__show-playing-age">
          <Checkbox
            htmlId="playingAgeCheckbox"
            label={content.publishActualAgeInfo}
            ariaLabel={content.publishActualAgeInfo}
            checked={isActualAgeVisible}
            value=""
            name="is-published-age"
            valueChanged={inputChanged("isActualAgeVisible")}
          />
        </div>
      </div>
    </ControlSection>
  );
};

export default withEditBasicInfoContext(PlayingAge);
